import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { useSelector } from 'react-redux';
import {
  Home,
  Trophy,
  Gamepad2,
  Target,
  Calendar,
  FileText,
  Star,
  MessageSquare,
  Settings,
  UserCheck,
} from "lucide-react";

const menuItems = [
  { title: "Dashboard", path: "/dashboard", icon: Home },
  { title: "My Achievements", path: "/dashboard/my-achievements", icon: Trophy },
  { title: "Learning Games", path: "/dashboard/learning-games", icon: Gamepad2 },
  { title: "My Progress", path: "/dashboard/my-progress", icon: Target },
  { title: "Attendance", path: "/dashboard/attendance", icon: UserCheck },
  { title: "School Calendar", path: "/dashboard/school-calendar", icon: Calendar },
  { title: "communication", path: "/dashboard/communication", icon: MessageSquare },
  { title: "Class Schedule", path: "/dashboard/class-schedule", icon: Calendar },
  { title: "Homework", path: "/dashboard/homework", icon: FileText },
  { title: "Skill Stars", path: "/dashboard/skill-stars", icon: Star },
  { title: "Settings", path: "/dashboard/settings", icon: Settings },
];

const StudentSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);

  const currentUser = useSelector((state: any) => state.users?.currentUser);
  const subscriptionStatus = currentUser?.subscriptionStatus;
  const subscriptionExpiry = currentUser?.subscriptionExpiry;
  const isExpiredByDate = subscriptionExpiry ? Date.now() > subscriptionExpiry : false;
  const isInactive = (subscriptionStatus === 'expired' || subscriptionStatus === 'paused' || !subscriptionStatus) || isExpiredByDate;
  // New: user is logged in but not yet enrolled in any school
  const hasNoSchool = !currentUser?.selectedAffiliation?.schoolID;

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    setActivePath(path);
    navigate(path);
  };

  return (
    <Sidebar
      className="w-48 sm:w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-black"
    >
      <SidebarContent className="p-2 sm:p-4 dark:bg-[#020817] dark:border-[#364153]">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
            <Star className="h-3 w-3" />
            Student Menu
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    isActive={activePath === item.path}
                    className="w-full justify-start cursor-pointer p-2 sm:p-3 rounded-lg transition-all duration-200 touch-manipulation text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 data-[active=true]:bg-blue-100 dark:data-[active=true]:bg-blue-900 data-[active=true]:text-blue-700 dark:data-[active=true]:text-blue-300"
                    onClick={() => {
                      // Unenrolled: only Settings is accessible
                      if (hasNoSchool && item.title !== "Settings") return;
                      if (isInactive && item.title !== "Settings") {
                        navigate('/subscription-expired');
                        return;
                      }
                      handleNavigation(item.path);
                    }}
                    style={hasNoSchool && item.title !== "Settings" ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                  >
                    <item.icon className="mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="text-sm sm:text-base truncate">{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default StudentSidebar;